import {
    Dictionary,
    ExcludeStrict,
    iterableFindAndSelect,
    objectMapValues,
    objectTryParse,
    Values
} from '../index';
import {IRoutesManagerWithContext, RoutesManager} from '../routes-manager/routes-manager'
import {RestfulApiHttpMethod} from './restful-api-http-method';


export type RestfulApiAsync<API> = {
    [key in keyof API]: API[key] extends ((...args: infer ARGS) => infer R) ?
        R extends Promise<any> ? API[key] : (...args: ARGS) => Promise<R>
        : never ;
};

export type RestfulApiMethodPaths<API> = {
    [key in keyof API]: API[key] extends ((...args: infer ARGS) => any) ?
        (...args: ARGS) => string
        : never ;
};

export type ExtractApiMethodParams<API, METHOD_NAME extends keyof API> =
    API[METHOD_NAME] extends (params: infer PARAMS) => any ? PARAMS : {};

export type RestfulApiParamsValidator<API> = {
    [key in keyof API] : API[key] extends (...params: infer PARAMS) => any ? ((...params: PARAMS) => void) : never;
}

export type ApiMethodRegistrationOptions<
    API,
    METHOD_NAME extends keyof API,
    PARAMS = ExtractApiMethodParams<API, METHOD_NAME>,
> = {
    params: Required<RestfulApiParamsMapping<PARAMS>>;
    method: RestfulApiHttpMethod;
    path: string;
    isLongTimeOperation?: boolean;
    useBeacon?: boolean;
}

interface ApiRoutesManagerContext {
    method: string;
    body: any;
    auth?: string;
}

export interface ApiMethodEndpointInfo<METHOD_NAME> {
    methodName: METHOD_NAME;
    urlParams: {};
    bodyParams?: {};
    params: {}
}

export type RestfulApiMethodParams<API_ENDPOINT_INFO extends ApiMethodEndpointInfo<unknown>> =
    API_ENDPOINT_INFO['urlParams'] &
    API_ENDPOINT_INFO['bodyParams'];

export type RestfulApiMethodParamsResolver<API_ENDPOINT_INFO extends ApiMethodEndpointInfo<unknown>> =
    (params: RestfulApiMethodParams<API_ENDPOINT_INFO>) => {
        urlParams?: object;
        bodyParams?: object;
        auth?: string;
    };

export interface RestfulApiMethodMetadata<API, API_ENDPOINT_INFO extends ApiMethodEndpointInfo<unknown>> {
    name: keyof API;
    path: string;
    method: RestfulApiHttpMethod;
    paramsResolver: RestfulApiMethodParamsResolver<API_ENDPOINT_INFO>;
    isLongTimeOperation: boolean;
    useBeacon: boolean;
}

export const RestfulApiParamType = {
    Url: 'url',
    Body: 'body',
    Auth: 'auth',
    Query: 'query'
} as const;

export interface RestfulApiQueryParamType {
    type: typeof RestfulApiParamType.Query,
    queryParam?: string;
}

export type RestfulApiParamType = Values<typeof RestfulApiParamType> | RestfulApiQueryParamType;
export type RestfulApiUrlParamType = 'url' | 'query' | RestfulApiQueryParamType;

export interface RestfulApiParamWithoutConverter<PARAM_TYPE extends RestfulApiParamType, OPTIONAL extends boolean> {
    type: PARAM_TYPE;
    isOptional: OPTIONAL;
}

export interface RestfulApiParamWithConverter<VALUE, PARAM_TYPE extends RestfulApiParamType, OPTIONAL extends boolean>
    extends RestfulApiParamWithoutConverter<PARAM_TYPE, OPTIONAL>
{
    convertToString: (value: VALUE) => string;
    fromString: (strValue: string) => VALUE;
}

export type RestfulApiParam<
    VALUE = unknown,
    PARAM_TYPE extends RestfulApiParamType = RestfulApiParamType,
    OPTIONAL extends boolean = boolean
> =
    RestfulApiParamWithoutConverter<PARAM_TYPE, OPTIONAL> |
    RestfulApiParamWithConverter<VALUE, PARAM_TYPE, OPTIONAL>;

export interface ApiParamConversionOptions<VALUE> {
    convertToString: (value: VALUE) => string;
    fromString: (strValue: string) => VALUE;
}

export function restfulApiUrlParamConverterCreateForStringEnum<T extends string> () : ApiParamConversionOptions<T> {
    return {
        fromString: (value: string) => value as T,
        convertToString: (value: T) => value
    }
}

export function restfulApiUrlParamConverterCreateForNumber () {
    return {
        fromString: (value: string) => parseInt(value),
        convertToString: (value: number) => value.toString()
    }
}

export function restfulApiUrlParamConverterCreateForBoolean (
    options: {
        trueStr?: string;
        falseStr?: string;
    } = {}
) {
    const {
        trueStr = '1',
        falseStr = '0'
    } = options;

    return {
        fromString: (value: string) => value === trueStr,
        convertToString: (value: boolean) => value ? trueStr : falseStr
    }
}

export function restfulApiQueryParamType (queryParam?: string) : RestfulApiQueryParamType {
    return {
        type: RestfulApiParamType.Query,
        queryParam: queryParam
    }
}


export function restfulApiParam<PARAM_TYPE extends ExcludeStrict<RestfulApiParamType, 'url'>> (
    paramType: PARAM_TYPE,
    isOptional: true
) : RestfulApiParamWithoutConverter<PARAM_TYPE, true>;
export function restfulApiParam<PARAM_TYPE extends RestfulApiParamType> (
    paramType: PARAM_TYPE,
    isOptional: false
) : RestfulApiParamWithoutConverter<PARAM_TYPE, false>;
export function restfulApiParam<VALUE, PARAM_TYPE extends ExcludeStrict<RestfulApiUrlParamType, 'url'>> (
    paramType: PARAM_TYPE,
    isOptional: true,
    conversionOptions: ApiParamConversionOptions<VALUE>
) : RestfulApiParamWithConverter<VALUE, PARAM_TYPE, true>;
export function restfulApiParam<VALUE, PARAM_TYPE extends RestfulApiUrlParamType> (
    paramType: PARAM_TYPE,
    isOptional: false,
    conversionOptions: ApiParamConversionOptions<VALUE>
) : RestfulApiParamWithConverter<VALUE, PARAM_TYPE, false>;
export function restfulApiParam<VALUE, PARAM_TYPE extends RestfulApiParamType> (
    paramType: PARAM_TYPE,
    isOptional: boolean,
    conversionOptions?: ApiParamConversionOptions<VALUE>
) : RestfulApiParamWithoutConverter<PARAM_TYPE, boolean> | RestfulApiParamWithConverter<VALUE, PARAM_TYPE, boolean>;
export function restfulApiParam<VALUE, PARAM_TYPE extends RestfulApiParamType> (
    paramType: PARAM_TYPE,
    isOptional: boolean,
    conversionOptions?: ApiParamConversionOptions<VALUE>
) : RestfulApiParamWithoutConverter<PARAM_TYPE, boolean> | RestfulApiParamWithConverter<VALUE, PARAM_TYPE, boolean> {
    return {
        type: paramType,
        isOptional: isOptional ?? false,
        ...conversionOptions
    }
}

export function restfulApiParamOptional<PARAM_TYPE extends ExcludeStrict<RestfulApiParamType, 'url'>> (
    paramType: PARAM_TYPE
) : RestfulApiParamWithoutConverter<PARAM_TYPE, true>;
export function restfulApiParamOptional<VALUE, PARAM_TYPE extends ExcludeStrict<RestfulApiUrlParamType, 'url'>> (
    paramType: PARAM_TYPE,
    conversionOptions: ApiParamConversionOptions<VALUE>
) : RestfulApiParamWithConverter<VALUE, PARAM_TYPE, true>;
export function restfulApiParamOptional<VALUE, PARAM_TYPE extends ExcludeStrict<RestfulApiParamType, 'url'>> (
    paramType: PARAM_TYPE,
    conversionOptions?: ApiParamConversionOptions<VALUE>
) : RestfulApiParamWithoutConverter<PARAM_TYPE, boolean> | RestfulApiParamWithConverter<VALUE, PARAM_TYPE, boolean> {
    return restfulApiParam<VALUE, PARAM_TYPE>(paramType, true, conversionOptions)
}


export function restfulApiParamRequired<PARAM_TYPE extends RestfulApiParamType> (
    paramType: PARAM_TYPE
) : RestfulApiParamWithoutConverter<PARAM_TYPE, false>;
export function restfulApiParamRequired<VALUE, PARAM_TYPE extends RestfulApiUrlParamType> (
    paramType: PARAM_TYPE,
    conversionOptions: ApiParamConversionOptions<VALUE>
) : RestfulApiParamWithConverter<VALUE, PARAM_TYPE, false>;
export function restfulApiParamRequired<VALUE, PARAM_TYPE extends RestfulApiParamType> (
    paramType: PARAM_TYPE,
    conversionOptions?: ApiParamConversionOptions<VALUE>
) : RestfulApiParamWithoutConverter<PARAM_TYPE, boolean> | RestfulApiParamWithConverter<VALUE, PARAM_TYPE, boolean> {
    return restfulApiParam<VALUE, PARAM_TYPE>(paramType, false, conversionOptions)
}

type RestfulApiParamsMapping<PARAMS> = {
    [key in keyof PARAMS]: string extends PARAMS[key] ?
        RestfulApiParamWithoutConverter<RestfulApiParamType, undefined extends PARAMS[key] ? true : false> :
        (
            | RestfulApiParamWithoutConverter<Exclude<RestfulApiParamType, RestfulApiUrlParamType>, undefined extends PARAMS[key] ? true : false>
            | RestfulApiParamWithConverter<Exclude<PARAMS[key], undefined>, RestfulApiParamType, undefined extends PARAMS[key] ? true : false>
        );
};


export class RestfulApiManager<
    API,
    METHOD_NAME extends keyof API & string = keyof API & string
>
    extends RoutesManager<ApiMethodEndpointInfo<METHOD_NAME>, ApiRoutesManagerContext>
    implements IRoutesManagerWithContext<ApiMethodEndpointInfo<METHOD_NAME>, ApiRoutesManagerContext>
{

    private apiMethodsInfo = new Map<METHOD_NAME, RestfulApiMethodMetadata<API, any>>();

    constructor(options: RoutesManager.Options) {
        super(true, options);
    }

    public readonly paths: RestfulApiMethodPaths<API> = new Proxy({}, {
        get: (_obj: any, propName: string) => {

            const apiMethodInfo = this.apiMethodsInfo.get(propName as METHOD_NAME);
            if (apiMethodInfo) {
                return (params: any) => {

                    const {
                        urlParams,
                        bodyParams
                    } = apiMethodInfo.paramsResolver(params);

                    return this.resolvePath({
                        methodName: apiMethodInfo.name,
                        urlParams: urlParams ?? {},
                        bodyParams: bodyParams
                    } as ApiMethodEndpointInfo<METHOD_NAME>)
                }
            } else {
                return undefined;
            }
        }
    });

    public registerApiMethod<
        REGISTERED_METHOD_NAME extends METHOD_NAME
    > (
        apiMethodName: REGISTERED_METHOD_NAME,
        options: ApiMethodRegistrationOptions<API, REGISTERED_METHOD_NAME>
    )  {
        const {
            path,
            method,
            isLongTimeOperation = false,
            useBeacon = false
        } = options;

        if (useBeacon && method !== RestfulApiHttpMethod.Post) {
            throw new Error(`useBeacon can be true only for a http post method (detected method '${method}')`)
        }

        const params = options.params as Dictionary<RestfulApiParam>;
        const paramsEntries = Object.entries(params);

        if (method === RestfulApiHttpMethod.Get) {

            paramsEntries.forEach(([name, param]) => {
                if (param.type === RestfulApiParamType.Body) {
                    throw new Error(`method '${apiMethodName}' has a body param '${name}'`)
                }
            })
        }

        const authParamName = iterableFindAndSelect(paramsEntries, ([paramName, param], skip) => {
            return param.type === RestfulApiParamType.Auth ? paramName : skip;
        })

        const queryParams: RoutesManager.RouteQueryParams<any> = {};
        for (const [paramName, param] of paramsEntries) {
            const paramType = param.type;

            if (paramType === RestfulApiParamType.Query || (typeof paramType === 'object' && paramType.type === RestfulApiParamType.Query)) {
                const queryParam = typeof paramType === 'object' && paramType.type === RestfulApiParamType.Query ?
                    (paramType.queryParam ?? paramName) :
                    paramName

                queryParams[queryParam] = param.isOptional ?
                    RoutesManager.queryParamOptional(paramName) :
                    RoutesManager.queryParamRequired(paramName);
            }
        }

        const paramsResolver = (allParams: Record<string, unknown>) => {
            const urlParams: Record<string, unknown> = {};
            const bodyParams: Record<string, unknown> = {};
            let hasUrlParams = false;
            let hasBodyParams = false;
            let auth: string | undefined = undefined;

            for (const [paramName, param] of paramsEntries) {

                const converter = (param as RestfulApiParamWithConverter<any, RestfulApiParamType, boolean>).convertToString;

                const paramType = param.type;

                const paramValue = allParams[paramName];

                if (paramValue !== undefined) {
                    if (paramType === RestfulApiParamType.Url) {
                        urlParams[paramName] = converter ? converter(paramValue as string) : paramValue;
                        hasUrlParams = true;
                    } else if (paramType === RestfulApiParamType.Body) {
                        bodyParams[paramName] = paramValue;
                        hasBodyParams = true;
                    } else if (paramType === RestfulApiParamType.Auth) {
                        if (auth !== undefined) {
                            throw new Error(`Can't have 2 params of 'Auth' type`)
                        } else if (typeof paramValue !== 'string') {
                            throw new Error(`param of 'Auth' type must have a string value`)
                        } else {
                            auth = paramValue;
                        }
                    } else {
                        // Query param.
                        urlParams[paramName] = converter ? converter(paramValue as string) : paramValue;
                        hasUrlParams = true;
                    }
                }


            }

            return {
                urlParams: hasUrlParams ? urlParams : undefined,
                bodyParams: hasBodyParams ? bodyParams : undefined,
                auth: auth
            }
        }

        this.apiMethodsInfo.set(apiMethodName as METHOD_NAME, {
            name: apiMethodName,
            path: path,
            method: method,
            paramsResolver: paramsResolver,
            isLongTimeOperation: isLongTimeOperation,
            useBeacon: useBeacon
        })

        this.registerRoute<any>({
            path: path,
            test: context => context.method.toUpperCase() === method.toUpperCase(),
            queryParams: queryParams,
            resolveRouteInfoFromParams: (match, context) => {

                const body = context.body;

                const bodyParams = (() => {
                    switch (typeof body) {
                        case 'string':
                            return objectTryParse(body);
                        case 'object':
                            return body;
                        default:
                            return undefined;
                    }
                })();

                const urlParams = objectMapValues(match, (value, key, skip) => {

                    if (value === undefined) {
                        return skip;
                    }

                    const paramInfo = params[key];

                    const converter = (paramInfo as RestfulApiParamWithConverter<any, RestfulApiParamType, boolean>).fromString;

                    if (converter) {
                        return converter(value)
                    } else {
                        return value;
                    }
                });


                return {
                    methodName: apiMethodName,
                    urlParams: urlParams,
                    bodyParams: bodyParams,
                    params: {
                        ...urlParams,
                        ...bodyParams,
                        ...(authParamName !== undefined ? {[authParamName]: context.auth} : {})
                    }
                };
            },
            resolveParams: routeInfo => {
                return routeInfo.methodName === apiMethodName ?
                    routeInfo.urlParams :
                    null
            }
        })
    }

    public getApiMethodsInfo () {
        return this.apiMethodsInfo;
    }
}
